import React, { useState } from 'react';
import ModalContainer from '@/components/molecules/ModalContainer';
import CustomButton from '@/components/atoms/CustomButton';
import { HStack, Icon, Text, Tooltip, VStack } from '@chakra-ui/react';
import svgIcons from '@/assets/svg/icons';
import { spinAnimation } from '@/constants/ui';
import { useForgotPassword } from '@/hooks/auth/resetPassword';

interface Props {
  isResendModalOpen: boolean;
  setIsResendModalOpen: (_: boolean) => void;
  email: string;
}

const MIN_SECONDS_TO_RESEND = 10;

const ForgotPasswordResendEmailModal = ({ isResendModalOpen, setIsResendModalOpen, email }: Props) => {
  const { mutate: resendForgotPassword, isPending: isResendingEmail } = useForgotPassword();

  const resendEmail = () => {
    setTimeout(() => setHasResentRecently(true), 200);
    setTimeout(() => setHasResentRecently(false), MIN_SECONDS_TO_RESEND * 1000);

    resendForgotPassword(email);
  };

  const [hasResentRecently, setHasResentRecently] = useState(false);

  return (
    <ModalContainer
      isBlur={false}
      isOpen={isResendModalOpen}
      onClose={() => setIsResendModalOpen(false)}
      width={'500px'}
      height={'300px'}
      backgroundColor="background.lightBlue"
      footerContent={
        <HStack justifyContent={'center'} width={'100%'} marginBottom={'10px'} gap={'30px'}>
          <Tooltip
            label={'You can send only one request per minute.'}
            visibility={hasResentRecently ? 'visible' : 'hidden'}
            padding={'14px'}
            borderRadius={'6px'}
            color={'text.darkBlue'}
            backgroundColor={'extra.white'}
            boxShadow={'0px 2px 8px 0px #00417933'}
            placement="right"
            top={10}
            right={10}
          >
            <VStack>
              <CustomButton
                isTransparent
                label={'Resend Email'}
                labelColor="primary.500"
                labelHoverColor={'primary.600'}
                backgroundColor={'transparent'}
                labelStyle={{ fontSize: '16px' }}
                onClick={resendEmail}
                height={'60px'}
                disabled={isResendingEmail || hasResentRecently}
                iconBeforeLabelJSX={
                  <Icon width={5} height={5} animation={isResendingEmail ? spinAnimation : 'none'} color="primary.500">
                    <svg width={'22.5'} height={'25'} viewBox={`0 0 13 15`} xmlns="http://www.w3.org/2000/svg">
                      <path d={svgIcons.reload} fill="currentColor" />
                    </svg>
                  </Icon>
                }
              />
            </VStack>
          </Tooltip>
        </HStack>
      }
    >
      <VStack marginTop={'20px'} gap={'20px'}>
        <Text variant={'loraSmallTitle'} color={'black'}>
          Check Your Email
        </Text>
        <Text variant={'urbanistMediumLarge'} textAlign={'center'} paddingX={'20px'} whiteSpace={'pre-wrap'}>
          We’ve sent a password reset link to {email}. Please check your inbox and follow the instructions in the email
          to reset your password.
        </Text>
        <Text variant={'urbanistMediumLarge'} textAlign={'center'} paddingX={'20px'} whiteSpace={'pre-wrap'}>
          If you don't receive it within a few minutes, resend the email by clicking the button below.
        </Text>
      </VStack>
    </ModalContainer>
  );
};

export default ForgotPasswordResendEmailModal;
